import { useState } from "react";
import type { ReactNode } from "react";
import styles from "./Password.module.scss";
import { Controller } from "react-hook-form";
import type { Control, RegisterOptions } from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import Eye from "@/components/SVGComponents/Eye";
import EyeSlash from "@/components/SVGComponents/EyeSlash";
import { InputAdornment, TextField } from "@mui/material";
import { combineClasses } from "@/lib/utils";

export interface PasswordProps {
  control?: Control<any>;
  register?: (name: string, options?: RegisterOptions) => Record<string, unknown>;
  validations?: RegisterOptions;
  name: string;
  placeholder?: string | undefined;
  startIcon?: ReactNode;
  className?: string | undefined;
  error?: boolean;
  [key: string]: unknown;
}

const Password = ({
  control,
  register,
  validations,
  name,
  placeholder,
  startIcon,
  className,
  error,
  ...rest
}: PasswordProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const renderInput = (field: Record<string, unknown> = {}) => (
    <TextField
      {...field}
      {...rest}
      id={name}
      label=""
      variant="outlined"
      type={showPassword ? "text" : "password"}
      className={combineClasses(
        styles.inputRoot,
        Boolean(error) && styles.error,
        typeof className === "string" ? className : undefined,
      )}
      placeholder={placeholder}
      slotProps={{
        input: {
          sx: {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px !important",
            },
          },
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : null,
          endAdornment: (
            <InputAdornment position="end">
              {showPassword ? (
                <EyeSlash onClick={toggleShowPassword} className={styles.eyeIcon} />
              ) : (
                <Eye onClick={toggleShowPassword} className={styles.eyeIcon} />
              )}
            </InputAdornment>
          ),
        },
        htmlInput: {
          className: combineClasses(styles.inputField, Boolean(startIcon) && styles.startIcon),
        },
      }}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        {...(validations ? { rules: validations } : {})}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else {
    const field = register ? register(name, validations) : {};
    return renderInput(field);
  }
};

const PasswordWithLabelAndError = withLabelAndError(Password);

export default PasswordWithLabelAndError;
export { Password };
